import { Icon } from '@iconify/react';
import { useState, useEffect } from 'react';

import { ChatWidget } from 'pages/maxi-ai/ChatWidget';
import { ChatSidebar } from 'pages/maxi-ai/sidebar/ChatSidebar';

import { withModuleErrorBoundary } from 'components/error-boundary/ModuleErrorBoundary';
import { Button } from 'components/general/Button';

import { useThreads } from 'hooks/maxi-ai/useThreads';

import { useMaxiAIStore } from 'store/maxi-ai.store';

interface ChatProps {
  onClose: () => void;
}

const Chat = withModuleErrorBoundary(({ onClose }: ChatProps) => {
  const [isClosing, setIsClosing] = useState(false);
  const [chatSidebarOpen, setChatSidebarOpen] = useState(false);
  const { isExpanded, setIsExpanded } = useMaxiAIStore();
  const { refetch } = useThreads();

  // Handle close animation
  const handleClose = () => setIsClosing(true);

  // Handle expand/collapse toggle
  const handleToggleExpand = () => setIsExpanded(!isExpanded);

  // Trigger onClose callback when animation completes
  const handleTransitionEnd = () => {
    if (isClosing) onClose();
  };

  // Fetch threads when chat opens
  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <div
      className="absolute top-20 bottom-20 z-[1000] shadow-lg rounded-2xl overflow-hidden bg-lime-1 border-[1px] border-lime-4 flex flex-col transition-all duration-300 ease-in-out"
      style={{
        right: '2rem',
        width: isExpanded ? 'calc(100% - 4rem)' : '40%',
        transform: isClosing
          ? 'translateX(calc(100% + 2rem))'
          : 'translateX(0)',
        opacity: isClosing ? 0 : 1,
      }}
      onTransitionEnd={handleTransitionEnd}
    >
      <div className="flex flex-col h-full relative">
        {/* Sidebar Overlay */}
        <ChatSidebar
          isOpen={chatSidebarOpen}
          onClose={() => setChatSidebarOpen(false)}
        />

        {/* Header with controls */}
        <div className="flex justify-between items-center p-4 pb-3">
          <div className="flex items-center gap-2">
            <Icon
              icon="ant-design:menu-unfold-outlined"
              className="text-xl cursor-pointer"
              onClick={() => setChatSidebarOpen(!chatSidebarOpen)}
            />
            <div className="font-medium text-base">Maxi AI</div>
          </div>

          <div className="flex gap-2 items-center relative z-[1000]">
            {/* Expand/collapse button */}
            <Button
              type="text"
              onClick={handleToggleExpand}
              icon={
                <Icon
                  icon={
                    isExpanded
                      ? 'ant-design:shrink-outlined'
                      : 'ant-design:expand-alt-outlined'
                  }
                  className="text-xl"
                />
              }
            />

            {/* Close button */}
            <Button
              type="text"
              onClick={handleClose}
              icon={
                <Icon icon="ant-design:close-outlined" className="text-xl" />
              }
            />
          </div>
        </div>

        {/* Chat content */}
        <div className="flex-1 min-h-0 p-4 pt-0 relative">
          {/* Sidebar backdrop overlay - only covers chat content area */}
          {chatSidebarOpen && (
            <div
              className="absolute inset-0 z-[999] bg-transparent"
              onClick={() => setChatSidebarOpen(false)}
            />
          )}
          <ChatWidget />
        </div>
      </div>
    </div>
  );
});

Chat.displayName = 'Chat';

export { Chat };
